import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="loop-video"
//
// Plays a muted, looping <video> only while someone is looking at it, so a page
// of twenty material clips downloads the ones actually watched. With the
// "hover" trigger a mouse decides (enter/leave actions); touch screens have no
// hover, so they fall back to playing whatever is on screen, like "visible".
//
// Sets data-playing on the video while it plays, for a fade over a still image.
// Data saver keeps the video stopped and the still showing. Reduced motion does
// not: Windows reports it whenever animation effects are off, which remote
// desktops, VMs and "best performance" settings do without the user asking.
//
// While a lightbox is open (lightbox:open/close on window) the video is paused
// and hidden: it is covered anyway, and Chrome's GPU video path can draw a
// playing page video over the lightbox's own.
export default class extends Controller {
  static values = { trigger: { type: String, default: "visible" } };

  connect() {
    // A Turbo snapshot taken mid-play would restore the faded-in state.
    this.element.removeAttribute("data-playing");

    if (this.#saveData) return;

    window.addEventListener("lightbox:open", this.#suspend);
    window.addEventListener("lightbox:close", this.#resume);

    if (this.triggerValue === "hover" && window.matchMedia("(hover: hover)").matches) {
      this.hoverable = true;
      return;
    }

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.isIntersecting;
        this.inView ? this.#play() : this.#pause();
      },
      { threshold: 0.5 },
    );
    this.observer.observe(this.element);
  }

  disconnect() {
    this.observer?.disconnect();
    window.removeEventListener("lightbox:open", this.#suspend);
    window.removeEventListener("lightbox:close", this.#resume);
    this.#pause();
  }

  enter() {
    if (this.hoverable) this.#play();
  }

  leave() {
    if (this.hoverable) this.#pause();
  }

  #suspend = () => {
    this.suspended = true;
    this.#pause();
    this.element.style.visibility = "hidden";
  };

  #resume = () => {
    this.suspended = false;
    this.element.style.visibility = "";
    if (this.inView) this.#play();
  };

  async #play() {
    if (this.suspended) return;

    try {
      await this.element.play();
    } catch {
      // Autoplay refused (Low Power Mode, browser policy): the still stays.
      return;
    }
    // play() resolves late; the pointer may have left or a lightbox opened.
    if (this.suspended) return this.#pause();
    if (!this.element.paused) this.element.setAttribute("data-playing", "");
  }

  #pause() {
    this.element.pause();
    this.element.removeAttribute("data-playing");
  }

  get #saveData() {
    return navigator.connection?.saveData === true;
  }
}
